import React, { useState, useContext } from 'react';
import { 
  Button,
  Tooltip,
  useToast
} from '@chakra-ui/react';
import { Heart } from 'lucide-react';
import { ConfigContext } from './Config';
import { useTranslation } from 'react-i18next';

const FavoriteButton = ({ itemId, initialIsFollowing = false, onFavoriteChange }) => {
  const { t } = useTranslation();
  const [isFollowing, setIsFollowing] = useState(initialIsFollowing);
  const [isLoading, setIsLoading] = useState(false);
  const config = useContext(ConfigContext);
  const toast = useToast();

  const handleToggleFavorite = async () => {
    if (!config.accessToken) {
      toast({
        title: t('myFavorites.loginRequired'),
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(
          `${config.backendUrl}/api/items/${itemId}?access_token=${config.accessToken}`,
          {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              favorite: !isFollowing
            })
          }
      );

      if (!response.ok) {
        throw new Error(isFollowing ?
            t('myFavorites.errorRemoving') :
            t('myFavorites.errorAdding')
        );
      }

      setIsFollowing(!isFollowing);

      if (onFavoriteChange) {
        onFavoriteChange(!isFollowing);
      }

      toast({
        title: isFollowing ?
            t('myFavorites.removeSuccess') :
            t('myFavorites.addSuccess'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const buttonText = isFollowing ? 
    t('myFavorites.following') : 
    t('myFavorites.followThisLot');

  const tooltipText = isFollowing ?
    t('myFavorites.removeTooltip') :
    t('myFavorites.addTooltip');

  return (
    <Tooltip 
      label={tooltipText}
      hasArrow
    >
      <Button
        variant={isFollowing ? "solid" : "outline"}
        colorScheme="gray"
        onClick={handleToggleFavorite}
        isLoading={isLoading}
        leftIcon={<Heart size={16} fill={isFollowing ? "currentColor" : "none"} />}
        size="sm"
      >
        {buttonText}
      </Button>
    </Tooltip>
  );
};

export default FavoriteButton;